import React, { useState, useMemo } from 'react';
import { 
    Dialog, DialogTitle, DialogContent, DialogActions, Button, 
    Typography, Box, Grid, Paper, IconButton, Chip, Divider,
    LinearProgress
} from '@mui/material';
import { 
    Close as CloseIcon, 
    BarChart as BarChartIcon, 
    TrendingUp as TrendingUpIcon, 
    TrendingDown as TrendingDownIcon, 
    Remove as MinusIcon,
    SupervisorAccount as SupervisorAccountIcon,
    Build as BuildIcon,
    Psychology as PsychologyIcon,
    Business as BusinessIcon
} from '@mui/icons-material';
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer, Tooltip as RechartsTooltip } from 'recharts';

const TabsComponent = ({ tabs }) => {
    const [activeTab, setActiveTab] = useState(0);

    const tabButtonStyle = (isActive) => ({
        padding: '10px 20px',
        border: 'none',
        borderBottom: isActive ? '3px solid #383838ff' : '3px solid transparent',
        backgroundColor: 'transparent',
        cursor: 'pointer',
        fontSize: '1rem',
        fontWeight: isActive ? '600' : '500',
        color: isActive ? '#383838ff' : '#475569',
        transition: 'all 0.2s ease-in-out',
    });

    const visibleTabs = tabs.filter(tab => tab.content !== null);

    return (
        <div>
            <div style={{ borderBottom: '1px solid #e2e8f0', marginBottom: '24px' }}>
                {visibleTabs.map((tab, index) => (
                    <button key={index} onClick={() => setActiveTab(index)} style={tabButtonStyle(activeTab === index)}>
                        {tab.label}
                    </button>
                ))}
            </div>
            <div>
                {visibleTabs[activeTab]?.content}
            </div>
        </div>
    );
};

const ComparativeRow = ({ label, currentScore, previousScore, type, areaAverageMock, competencyArea, userArea }) => {
    
    // Lógica de Variação (Histórico vs Atual)
    let delta = null;
    let deltaColor = '#94a3b8';
    let DeltaIcon = MinusIcon;

    if (previousScore !== undefined && previousScore !== null && previousScore > 0) {
        delta = (currentScore - previousScore).toFixed(1);
        if (delta > 0) { deltaColor = '#16a34a'; DeltaIcon = TrendingUpIcon; }
        else if (delta < 0) { deltaColor = '#ef4444'; DeltaIcon = TrendingDownIcon; }
    }

    // --- LÓGICA DE VISIBILIDADE DA MÉDIA ---
    const isBehavioral = ['Corporativa', 'Gerencial', 'Outra'].includes(type);
    const isTechnicalNative = type === 'Técnica' && (!competencyArea || competencyArea === userArea);
    const showAverage = isBehavioral || isTechnicalNative;
    
    return (
        <Grid container spacing={2} sx={{ py: 1.5, borderBottom: '1px solid #f1f5f9', alignItems: 'center' }}>
            {/* 1. Nome da Competência */}
            <Grid item xs={5}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#334155' }}>
                    {label}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Typography variant="caption" sx={{ color: '#94a3b8' }}>{type}</Typography>
                    {!isTechnicalNative && type === 'Técnica' && competencyArea && (
                         <Chip label={competencyArea} size="small" sx={{ fontSize: '0.65rem', height: 18, backgroundColor: '#fffbeb', color: '#f59e0b', border: '1px solid #fde68a' }} />
                    )}
                </Box>
            </Grid>

            {/* 2. Nota Atual + Indicador de Status */}
            <Grid item xs={2} sx={{ textAlign: 'center' }}>
                <Typography variant="body1" sx={{ fontWeight: 700, color: '#1e293b' }}>
                    {Number(currentScore).toFixed(1)}
                </Typography>
                
                {showAverage && areaAverageMock && (
                    <Chip 
                        label={currentScore >= areaAverageMock ? 'Acima da média' : 'Abaixo da média'} 
                        size="small" 
                        sx={{ 
                            fontSize: '0.65rem', 
                            height: 18, 
                            mt: 0.5,
                            backgroundColor: currentScore >= areaAverageMock ? '#dcfce7' : '#fee2e2', 
                            color: currentScore >= areaAverageMock ? '#166534' : '#991b1b',
                            fontWeight: 600
                        }} 
                    />
                )}
            </Grid>

            {/* 3. Comparativo com Histórico */}
            <Grid item xs={2} sx={{ textAlign: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, color: deltaColor }}>
                    {previousScore !== null && previousScore > 0 ? (
                        <>
                            <DeltaIcon fontSize="small" />
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{delta > 0 ? '+' : ''}{delta}</Typography>
                        </>
                    ) : (
                        <Typography variant="caption" sx={{ color: '#cbd5e1' }}>Sem histórico</Typography>
                    )}
                </Box>
            </Grid>

            {/* 4. Média da Área (Valor de Referência) */}
            <Grid item xs={3} sx={{ textAlign: 'center' }}>
                {showAverage && areaAverageMock ? (
                    <Typography variant="body1" sx={{ fontWeight: 600, color: '#64748b' }}>
                        {areaAverageMock}
                    </Typography>
                ) : (
                    <Typography variant="caption" sx={{ color: '#94a3b8', fontStyle: 'italic', lineHeight: 1.2, display: 'block' }}>
                        Média indisponível
                    </Typography>
                )}
            </Grid>
        </Grid>
    );
};

const ComparativeAnalysis = ({ node, overallAverage, technicalAverage, behavioralAverage, technicalData, otherData, corporateData, managerialData }) => {
    const [filter, setFilter] = useState('all');

    // 1. Obter Histórico Anterior
    const previousEvaluation = useMemo(() => {
        const history = node.data.evaluationHistory || [];
        if (history.length > 0) {
            return [...history].sort((a, b) => new Date(b.date) - new Date(a.date))[0];
        }
        return null;
    }, [node]);

    // Mocks de dados (REMOVED RANDOM DATA - Using NULL/Placeholder)
    const companyAvg = null; // "4.20"; 
    const areaAvg = null; // "4.05";
    const companyTechAvg = null;
    const companyBehavAvg = null;
    const areaTechAvg = null;
    const areaBehavAvg = null;

    // Área do Colaborador
    const userAreaName = node.data.areaName;

    // Combinar dados para a lista
    const allCompetencies = [
        ...technicalData.map(d => ({ ...d, type: 'Técnica' })),
        ...otherData.map(d => ({ ...d, type: 'Outra' })),
        ...corporateData.map(d => ({ ...d, type: 'Corporativa' })),
        ...managerialData.map(d => ({ ...d, type: 'Gerencial' }))
    ];

    const filteredList = allCompetencies.filter(comp => {
        if (filter === 'all') return true;
        if (filter === 'technical') return comp.type === 'Técnica';
        if (filter === 'behavioral') return comp.type === 'Corporativa' || comp.type === 'Outra' || comp.type === 'Gerencial';
        return true;
    });

    const ProgressBar = ({ value, color, label }) => (
        <Box sx={{ mb: 1.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="caption" sx={{ fontWeight: 500, color: '#475569' }}>{label}</Typography>
                <Typography variant="caption" sx={{ fontWeight: 700, color: color }}>{value || '-'}</Typography>
            </Box>
            <LinearProgress 
                variant="determinate" 
                value={value ? (parseFloat(value) / 5) * 100 : 0} 
                sx={{ 
                    height: 6, 
                    borderRadius: 3, 
                    backgroundColor: '#e2e8f0',
                    '& .MuiLinearProgress-bar': { backgroundColor: color, borderRadius: 3 }
                }} 
            />
        </Box>
    );

    return (
        <Box sx={{ mt: 4, pt: 4, borderTop: '2px solid #e2e8f0' }}>
            <Typography variant="h6" sx={{ color: '#1e293b', fontWeight: 700, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                <BarChartIcon /> Análise Comparativa Detalhada
            </Typography>

            <Grid container spacing={4}>
                
                {/* COLUNA ESQUERDA: Resumos */}
                <Grid item xs={12} md={4}>
                    <Paper elevation={0} sx={{ p: 3, backgroundColor: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                        
                        <Typography variant="subtitle2" sx={{ color: '#334155', mb: 2, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                            Comparativo Geral
                        </Typography>
                        <ProgressBar value={overallAverage} color="#3b82f6" label="Colaborador (Geral)" />
                        {areaAvg && <ProgressBar value={areaAvg} color="#f59e0b" label="Média da Área" />}
                        {companyAvg && <ProgressBar value={companyAvg} color="#10b981" label="Média da Empresa" />}

                        <Divider sx={{ my: 3 }} />

                        <Typography variant="subtitle2" sx={{ color: '#334155', mb: 2, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                            Médias Específicas
                        </Typography>

                        <Box sx={{ mb: 3 }}>
                            <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, color: '#8b5cf6', mb: 1, letterSpacing: 0.5 }}>
                                TÉCNICAS
                            </Typography>
                            <ProgressBar value={technicalAverage} color="#8b5cf6" label="Colaborador" />
                            {areaTechAvg && <ProgressBar value={areaTechAvg} color="#f59e0b" label="Média da Área" />}
                        </Box>

                        <Box>
                            <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, color: '#ec4899', mb: 1, letterSpacing: 0.5 }}>
                                COMPORTAMENTAIS
                            </Typography>
                            <ProgressBar value={behavioralAverage} color="#ec4899" label="Colaborador" />
                            {areaBehavAvg && <ProgressBar value={areaBehavAvg} color="#f59e0b" label="Média da Área" />}
                        </Box>
                    </Paper>
                </Grid>

                {/* COLUNA DIREITA: Lista */}
                <Grid item xs={12} md={8}>
                    <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                        {['all', 'technical', 'behavioral'].map((f) => (
                            <Chip 
                                key={f}
                                label={f === 'all' ? 'Todos' : (f === 'technical' ? 'Técnicas' : 'Comportamentais')}
                                onClick={() => setFilter(f)}
                                variant={filter === f ? 'filled' : 'outlined'}
                                color={filter === f ? 'primary' : 'default'}
                                sx={{ fontWeight: 500 }}
                            />
                        ))}
                    </Box>

                    <Paper elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden' }}>
                        <Box sx={{ display: 'grid', gridTemplateColumns: '5fr 2fr 2fr 3fr', gap: 2, p: 2, backgroundColor: '#f1f5f9', borderBottom: '1px solid #e2e8f0' }}>
                            <Typography variant="caption" sx={{ fontWeight: 700, color: '#475569' }}>Competência</Typography>
                            <Typography variant="caption" sx={{ fontWeight: 700, color: '#475569', textAlign: 'center' }}>Nota Atual</Typography>
                            <Typography variant="caption" sx={{ fontWeight: 700, color: '#475569', textAlign: 'center' }}>Evolução</Typography>
                            <Typography variant="caption" sx={{ fontWeight: 700, color: '#475569', textAlign: 'center' }}>Média Área</Typography>
                        </Box>

                        <Box sx={{ maxHeight: 450, overflowY: 'auto' }}>
                            {filteredList.map((comp, idx) => {
                                const prevScore = previousEvaluation?.evaluations?.[comp.subject]?.rating || null;
                                // Placeholder for Area Average - would come from backend
                                const areaAvgMock = null; 

                                // Busca dados extras
                                let originalComp = null;
                                if (comp.type === 'Técnica') {
                                    originalComp = (node.data.competenciasTecnicas || []).find(c => c.nome === comp.subject);
                                }
                                const compArea = originalComp ? originalComp.areaName : null;

                                return (
                                    <Box key={idx} sx={{ px: 2 }}>
                                        <ComparativeRow 
                                            label={comp.subject}
                                            type={comp.type}
                                            currentScore={comp.score}
                                            previousScore={prevScore}
                                            areaAverageMock={areaAvgMock}
                                            competencyArea={compArea} 
                                            userArea={userAreaName}   
                                        />
                                    </Box>
                                );
                            })}
                            {filteredList.length === 0 && (
                                <Box sx={{ p: 4, textAlign: 'center', color: '#94a3b8', fontStyle: 'italic' }}>
                                    Nenhuma competência encontrada para este filtro.
                                </Box>
                            )}
                        </Box>
                    </Paper>
                </Grid>

            </Grid>
        </Box>
    );
};

const EvaluationResultModal = ({ isOpen, onClose, node, dicionarioCompetencias }) => {
    if (!isOpen || !node) return null;

    const evaluations = node.data.evaluations || {};
    const technicalCompetencies = node.data?.competenciasTecnicas || [];
    const otherCompetencies = node.data?.outrasCompetencias || [];
    const corporateCompetencies = dicionarioCompetencias?.corporativas || [];
    const managerialCompetencies = node.data?.isGerencial ? (dicionarioCompetencias?.gerenciais || []) : [];

    // Função auxiliar para criar dados do gráfico e cálculo
    const createChartData = (competencies) => {
        return competencies.map(comp => ({
            subject: comp.nome,
            score: evaluations[comp.nome]?.rating || 0,
            fullMark: 5
        }));
    };

    // Calcula a média APENAS das competências que possuem nota (score > 0)
    const calculateAverage = (data) => {
        if (!data || data.length === 0) return "0.00";
        
        // Filtra apenas os itens que foram avaliados (score maior que 0)
        const ratedItems = data.filter(item => item.score > 0);

        // Se nenhum item foi avaliado, a média é 0
        if (ratedItems.length === 0) return "0.00";

        const totalScore = ratedItems.reduce((acc, item) => acc + item.score, 0);
        return (totalScore / ratedItems.length).toFixed(2);
    };

    // Prepara dados
    const technicalData = createChartData(technicalCompetencies);
    const otherData = createChartData(otherCompetencies);
    const corporateData = createChartData(corporateCompetencies);
    const managerialData = createChartData(managerialCompetencies);

    // Calcula médias individuais
    const technicalAverage = calculateAverage(technicalData);
    const otherAverage = calculateAverage(otherData);
    const corporateAverage = calculateAverage(corporateData);
    const managerialAverage = calculateAverage(managerialData);
    
    // Calcula média comportamental e geral
    const behavioralAverage = calculateAverage([...corporateData, ...managerialData, ...otherData]);
    const overallAverage = calculateAverage([...technicalData, ...otherData, ...corporateData, ...managerialData]);

    // Componente interno para Card de Média
    const AverageCard = ({ label, value, color }) => (
        <Paper elevation={0} sx={{ 
            textAlign: 'center', p: 2, borderRadius: '12px', border: '2px solid #e2e8f0', 
            transition: 'transform 0.2s, box-shadow 0.2s',
            '&:hover': { transform: 'translateY(-4px)', boxShadow: `0 8px 16px ${color}30`, borderColor: color }
        }}>
            <Typography variant="h4" sx={{ fontWeight: 'bold', color: color, lineHeight: 1, mb: 1 }}>
                {value}
            </Typography>
            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                {label}
            </Typography>
        </Paper>
    );

    // Componentes internos para o Gráfico Radar (Eixos e Labels)
    const CustomizedAxisTick = (props) => {
        const { x, y, payload, textAnchor } = props;
        const words = payload.value.split(' ');
        const MAX_CHARS_PER_LINE = 15;
        const lines = [];
        let currentLine = words[0];

        for (let i = 1; i < words.length; i++) {
            const word = words[i];
            if ((currentLine + ' ' + word).length <= MAX_CHARS_PER_LINE) {
                currentLine += ' ' + word;
            } else {
                lines.push(currentLine);
                currentLine = word;
            }
        }
        lines.push(currentLine);

        const verticalOffset = lines.length > 1 ? -(lines.length - 1) * 7 : 0;

        return (
            <g transform={`translate(${x},${y + verticalOffset})`}>
                <text x={0} y={0} dy={4} textAnchor={textAnchor || 'middle'} fill="#475569" fontSize="12" fontWeight="500">
                    {lines.map((line, index) => (
                        <tspan x={0} dy={index === 0 ? '1.2em' : '1.2em'} key={index}>{line}</tspan>
                    ))}
                </text>
            </g>
        );
    };

    const CustomizedRadiusTick = ({ payload, x, y }) => (
        <text x={x} y={y} textAnchor="middle" fill="#9ca3af" fontSize="12px" dy={4}>
            {payload.value}
        </text>
    );

    const ChartComponent = ({ data, color, title }) => (
        <Paper elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: '12px', p: 3, mb: 3 }}>
            <Typography variant="h6" sx={{ textAlign: 'center', mb: 2, fontWeight: 700, color: '#1e293b', borderBottom: '2px solid #f1f5f9', pb: 1 }}>
                {title}
            </Typography>
            {data.length > 0 ? (
                <Box sx={{ width: '100%', height: 450 }}>
                    <ResponsiveContainer>
                        <RadarChart cx="50%" cy="50%" outerRadius="70%" data={data} margin={{ top: 30, right: 50, left: 50, bottom: 30 }}>
                            <PolarGrid gridType="polygon" stroke="#e2e8f0" />
                            <PolarAngleAxis dataKey="subject" tick={<CustomizedAxisTick />} />
                            <PolarRadiusAxis angle={90} domain={[0, 5]} tickCount={6} tick={<CustomizedRadiusTick />} axisLine={false} tickLine={false} />
                            <Radar name={node.data.name} dataKey="score" stroke={color} fill={color} fillOpacity={0.6} strokeWidth={2} />
                            <RechartsTooltip contentStyle={{ backgroundColor: 'rgba(255, 255, 255, 0.95)', border: '2px solid #e2e8f0', borderRadius: '8px', backdropFilter: 'blur(4px)', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)' }} />
                        </RadarChart>
                    </ResponsiveContainer>
                </Box>
            ) : (
                <Typography sx={{ textAlign: 'center', color: '#94a3b8', py: 4, fontStyle: 'italic' }}>
                    Nenhum dado disponível para exibir o gráfico.
                </Typography>
            )}
        </Paper>
    );

    const chartTabs = [
        {
            label: `Técnicas (${technicalData.length})`,
            content: <ChartComponent data={technicalData} color="#8b5cf6" title="Competências Técnicas" />
        },
        {
            label: `Outras (${otherData.length})`,
            content: <ChartComponent data={otherData} color="#ec4899" title="Outras Competências" />
        },
        {
            label: `Corporativas (${corporateData.length})`,
            content: <ChartComponent data={corporateData} color="#14b8a6" title="Competências Corporativas" />
        },
        ...(managerialData.length > 0 ? [{
            label: `Gerenciais (${managerialData.length})`,
            content: <ChartComponent data={managerialData} color="#f59e0b" title="Competências Gerenciais" />
        }] : [])
    ];

    return (
        <Dialog open={isOpen} onClose={onClose} maxWidth="lg" fullWidth PaperProps={{ sx: { borderRadius: '16px', maxHeight: '90vh' } }}>
            {/* Header */}
            <DialogTitle style={{ background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)', color: 'white' }} sx={{ py: 3, px: 4, borderBottom: '3px solid #22d3ee', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1, color: 'white' }}>
                        <BarChartIcon fontSize="large" /> Resultados da Avaliação
                    </Typography>
                    <Typography variant="subtitle1" sx={{ mt: 1, opacity: 0.9 }}>
                        Colaborador: <strong>{node.data.name}</strong>
                    </Typography>
                </Box>
                <IconButton onClick={onClose} sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.2)', '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' } }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            {/* Body */}
            <DialogContent sx={{ p: 4, bgcolor: '#ffffff' }}>
                {/* Médias por Competência (Cards do Topo) */}
                <Box sx={{ mb: 4 }}>
                    <Typography variant="h6" sx={{ color: '#1e293b', fontWeight: 700, mb: 2, pb: 1, borderBottom: '2px solid #e2e8f0' }}>
                        📊 Resumo das Médias (Considerando apenas itens avaliados)
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6} md={2.4}>
                            <AverageCard label="Geral" value={overallAverage} color="#1e293b" />
                        </Grid>
                        {technicalData.length > 0 && (
                            <Grid item xs={12} sm={6} md={2.4}>
                                <AverageCard label="Técnicas" value={technicalAverage} color="#8b5cf6" />
                            </Grid>
                        )}
                        {otherData.length > 0 && (
                            <Grid item xs={12} sm={6} md={2.4}>
                                <AverageCard label="Outras" value={otherAverage} color="#ec4899" />
                            </Grid>
                        )}
                        {corporateData.length > 0 && (
                            <Grid item xs={12} sm={6} md={2.4}>
                                <AverageCard label="Corporativas" value={corporateAverage} color="#14b8a6" />
                            </Grid>
                        )}
                        {managerialData.length > 0 && (
                            <Grid item xs={12} sm={6} md={2.4}>
                                <AverageCard label="Gerenciais" value={managerialAverage} color="#f59e0b" />
                            </Grid>
                        )}
                    </Grid>
                </Box>

                {/* Gráficos em Abas */}
                <TabsComponent tabs={chartTabs} />

                {/* Painel de Comparativo Avançado */}
                <ComparativeAnalysis 
                    node={node}
                    overallAverage={overallAverage}
                    technicalAverage={technicalAverage}
                    behavioralAverage={behavioralAverage}
                    technicalData={technicalData}
                    otherData={otherData}
                    corporateData={corporateData}
                    managerialData={managerialData}
                />

            </DialogContent>

            {/* Footer */}
            <DialogActions sx={{ p: 3, bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
                <Button onClick={onClose} variant="outlined" color="inherit">
                    Fechar
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default EvaluationResultModal;
